import { useEffect } from 'react';

/**
 * Dynamic SEO component.
 * Sets <title>, <meta>, canonical, OG, Twitter card and JSON-LD structured data
 * per page. Cleans up the JSON-LD it added on unmount/route change.
 */
const setMeta = (selector, attr, name, content) => {
  if (content == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const SEO = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  canonical,
  jsonLd,
}) => {
  useEffect(() => {
    if (title) document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[name="keywords"]', 'name', 'keywords', keywords);

    // Open Graph
    setMeta('meta[property="og:title"]', 'property', 'og:title', ogTitle || title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', ogDescription || description);
    if (ogImage) setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    if (canonical) setMeta('meta[property="og:url"]', 'property', 'og:url', canonical);

    // Twitter
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', ogTitle || title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', ogDescription || description);
    if (ogImage) setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // Canonical
    if (canonical) {
      let canonicalLink = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonical;
    }

    // og:type fallback
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');

    // JSON-LD: support a single object or an array of schemas
    const injected = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-dynamic', 'true');
        script.text = JSON.stringify(schema);
        document.head.appendChild(script);
        injected.push(script);
      });
    }

    return () => {
      injected.forEach((node) => node.parentNode && node.parentNode.removeChild(node));
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, canonical, jsonLd]);

  return null;
};

export default SEO;
